import { Col, Fade, Form, Row } from "react-bootstrap";
import { FaX } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import { UserContext } from "context/UserContext";
import { useContext, useState } from "react";
import { SingleValue } from "react-select";
import converterMesToString from "shared/util/functions/conversorDeMes/ConversorDeMes";
import InputSearch from "shared/components/input/InputSearch";
import CustomToggleButton from "shared/components/button/CustomToggleButton";
import CustomSelect from "shared/components/select/CustomSelect";
import { SelectOptionType } from "types/select/SelectOptionType";
import ItemListaDespesaGroupPay from "./ItemListaDespesaGroupPay";
import NenhumaDespesaEncontradaGroupPay from "./NenhumaDespesaEncontradaGroupPay";
import { BuscarDespesaGroupPayResponse } from "types/response/groupPay/BuscarDespesaGroupPayResponse";
import { DespesaGroupPayAgrupadaResponse } from "types/response/groupPay/DespesaGroupPayAgrupadaResponse";
import { formatarDataParaGetRequest } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { Pagination } from "@mui/material";
import { useGetDespesasGroupPay } from "hooks/groupPay/useGetDespesasGroupPay";

const OPCOES_TIPO_BUSCA: SelectOptionType<string>[] = [
  { value: "TODOS", label: "Todos" },
  { value: "IA", label: "IA" },
  { value: "EMAIL", label: "E-mail" },
];

const ORIGENS_TIPO_EMAIL = ["BOLETO", "MAIL_READER"];
const ORIGEM_TIPO_IA = "NOTA_FISCAL";

const filtrarRegistrosPorTipoBusca = (
  registros: BuscarDespesaGroupPayResponse[],
  tipoBuscaValue: string | undefined
): BuscarDespesaGroupPayResponse[] => {
  if (!tipoBuscaValue || tipoBuscaValue === "TODOS") return registros;
  if (tipoBuscaValue === "IA") {
    return registros.filter((d) => d.origemDespesa === ORIGEM_TIPO_IA);
  }
  if (tipoBuscaValue === "EMAIL") {
    return registros.filter((d) => ORIGENS_TIPO_EMAIL.includes(d.origemDespesa));
  }
  return registros;
};

const NUMEROS_DOC_SEM_AGRUPAMENTO = new Set([
  "",
  "-",
  "--",
  "—",
  "n/a",
  "na",
  "s/n",
  "sn",
]);

const numeroDocumentoValidoParaAgrupamento = (numeroDoc: string | null | undefined): boolean => {
  const raw = numeroDoc?.trim() ?? "";
  const chave = raw.toLowerCase().replace(/\s+/g, "");
  return Boolean(raw && !NUMEROS_DOC_SEM_AGRUPAMENTO.has(chave));
};

const montarChaveAgrupamentoDespesa = (despesa: BuscarDespesaGroupPayResponse): string => {
  const uuidNf = despesa.uuidNotaFiscalOrigem?.trim();
  if (uuidNf) {
    return `nf_${uuidNf}`;
  }

  const fornecedorKey = `${despesa.cpfCnpjEmissor ?? ""}_${despesa.nomeEmissor ?? ""}`;
  const condo = String(despesa.condominioId ?? "");

  if (numeroDocumentoValidoParaAgrupamento(despesa.numeroDoc)) {
    return `doc_${despesa.numeroDoc!.trim()}_${fornecedorKey}_${condo}`;
  }

  const valorKey = Number(despesa.valor).toFixed(2);
  return `sem_nf_${condo}_${fornecedorKey}_${valorKey}`;
};

const ordenarGrupoPorVencimento = (grupo: BuscarDespesaGroupPayResponse[]): BuscarDespesaGroupPayResponse[] => {
  return [...grupo].sort((a, b) => {
    const ta = a.dataVencimento ? new Date(a.dataVencimento).getTime() : 0;
    const tb = b.dataVencimento ? new Date(b.dataVencimento).getTime() : 0;
    return ta - tb;
  });
};

const TelaListaDespesasGroupPay: React.FC = () => {
  const location = useLocation();
  const { competenciaSelecionadaOnChange, condominioContexto } = useContext(UserContext);

  const [isHistorico, setIsHistorico] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pesquisa, setPesquisa] = useState("");
  const [numeroDoc, setNumeroDoc] = useState("");
  const [tipoBusca, setTipoBusca] = useState<SingleValue<SelectOptionType<string>>>(OPCOES_TIPO_BUSCA[0]);

  const condominioId = location.state?.condominioId || condominioContexto?.id;

  const parametrosPesquisa = {
    page: page - 1,
    sort: "",
    size: 9999,
    pesquisa: pesquisa,
    numeroDoc: numeroDoc || undefined,
    tipoBusca: tipoBusca?.value ?? undefined,
    condominioId: condominioId,
    isHistorico: isHistorico,
    mesCompetenciaSincronizacao: isHistorico
      ? ""
      : competenciaSelecionadaOnChange
        ? formatarDataParaGetRequest(competenciaSelecionadaOnChange)
        : "",
  };

  const { data, isLoading } = useGetDespesasGroupPay(parametrosPesquisa);

  const agruparDespesasPorNumeroDocumento = (
    despesas: BuscarDespesaGroupPayResponse[]
  ): (BuscarDespesaGroupPayResponse | DespesaGroupPayAgrupadaResponse)[] => {
    const despesasNaoExcluidas = despesas.filter((d) => !d.excluida);
    const despesasExcluidas = despesas.filter((d) => d.excluida);

    const gruposPorNumeroDoc = new Map<string, BuscarDespesaGroupPayResponse[]>();

    despesasNaoExcluidas.forEach((despesa) => {
      const key = montarChaveAgrupamentoDespesa(despesa);
      if (!gruposPorNumeroDoc.has(key)) {
        gruposPorNumeroDoc.set(key, []);
      }
      gruposPorNumeroDoc.get(key)!.push(despesa);
    });

    const resultado: (BuscarDespesaGroupPayResponse | DespesaGroupPayAgrupadaResponse)[] = [];

    gruposPorNumeroDoc.forEach((grupo, key) => {
      if (grupo.length > 1) {
        const primeiroFornecedor = grupo[0].cpfCnpjEmissor;
        const primeiroNomeFornecedor = grupo[0].nomeEmissor;
        const todasTemMesmoFornecedor = grupo.every(
          (d) => d.cpfCnpjEmissor === primeiroFornecedor && d.nomeEmissor === primeiroNomeFornecedor
        );

        if (todasTemMesmoFornecedor) {
          const grupoOrdenado = ordenarGrupoPorVencimento(grupo);
          const primeiraDespesa = grupoOrdenado[0];
          const valorTotal = grupoOrdenado.reduce((soma, d) => soma + d.valor, 0);
          const ids = grupoOrdenado.map((d) => d.id);

          const despesaAgrupada: DespesaGroupPayAgrupadaResponse = {
            ...primeiraDespesa,
            valor: valorTotal,
            ids,
            despesasOriginais: grupoOrdenado,
            quantidadeDespesas: grupo.length,
            isAgrupada: true,
          };

          resultado.push(despesaAgrupada);
        } else {
          grupo.forEach((despesa) => resultado.push(despesa));
        }
      } else {
        resultado.push(grupo[0]);
      }
    });

    despesasExcluidas.forEach((despesa) => {
      resultado.push(despesa);
    });

    return resultado;
  };

  const renderContentListaDespesasGroupPay = () => {
    return (
      <div className="p-3">
        {renderHeaderListaDespesas()}
        {!isLoading && renderBodyListaDespesas()}
      </div>
    );
  };

  const renderHeaderListaDespesas = () => {
    return (
      <div>
        {renderTitulo()}
        <div>
          <div className="mt-4">{renderCamposDoFiltro()}</div>
        </div>
      </div>
    );
  };

  const renderTitulo = () => {
    const dataFormatada = `${converterMesToString(
      competenciaSelecionadaOnChange.getMonth()
    )}/${competenciaSelecionadaOnChange.getFullYear()}`;

    return (
      <div>
        <div className="font-size-18-rem">
          {!isHistorico ? (
            <>
              Despesas enviadas de <span className="font-weight-600">{dataFormatada}</span>
            </>
          ) : (
            <>Histórico de despesas pendente aprovação</>
          )}
        </div>
      </div>
    );
  };

  const renderCamposDoFiltro = () => {
    return (
      <Row className="align-items-end">
        {renderCampoBusca()}
        {renderCampoNumeroDocumento()}
        {renderCampoTipoBusca()}
        {renderHistorico()}
      </Row>
    );
  };

  const renderCampoBusca = () => {
    return (
      <Col sm={3} xs={12} lg={3}>
        <InputSearch
          id="buscar-despesas-captadas-ia-group-pay"
          key="key-buscar-despesas-captadas-ia-group-pay"
          label="Buscar por condomínio, fornecedor, ou CNPJ"
          placeholder="informe aqui a sua busca"
          onSearchChange={setPesquisa}
        />
      </Col>
    );
  };

  const renderCampoNumeroDocumento = () => {
    return (
      <Col sm={2} xs={12} lg={2}>
        <Form.Group>
          <Form.Label className="label-input">Número do documento</Form.Label>
          <Form.Control
            id="numero-documento-despesas-group-pay"
            type="text"
            placeholder=""
            value={numeroDoc}
            onChange={(e) => setNumeroDoc(e.target.value)}
            className="input-gc"
          />
        </Form.Group>
      </Col>
    );
  };

  const renderCampoTipoBusca = () => {
    return (
      <Col sm={2} xs={12} lg={2}>
        <CustomSelect
          id="tipo-busca-despesas-group-pay"
          label="Tipo de busca"
          options={OPCOES_TIPO_BUSCA}
          usarValidacaoParaFormulario={false}
          placeholder="Selecione"
          value={tipoBusca}
          onChange={(opcao) => setTipoBusca(opcao)}
        />
      </Col>
    );
  };

  const renderHistorico = () => {
    return (
      <Col className="text-end">
        <CustomToggleButton
          value={isHistorico}
          onChange={() => {
            setIsHistorico(!isHistorico);
          }}
          label="Histórico"
          checked={isHistorico}
          variant={isHistorico ? "dark" : "light"}
          rightIcon={isHistorico ? FaX : undefined}
        />
      </Col>
    );
  };

  const renderBodyListaDespesas = () => {
    if (!data?.payload) {
      return <NenhumaDespesaEncontradaGroupPay />;
    }
    const registrosBrutos = (data.payload.content ?? []) as BuscarDespesaGroupPayResponse[];
    const registros = filtrarRegistrosPorTipoBusca(registrosBrutos, tipoBusca?.value);
    const totalElementos = registros.length;
    if (totalElementos === 0) {
      return (
        <div>
          <NenhumaDespesaEncontradaGroupPay />
          <div className="mt-4 d-flex justify-content-end">{renderPaginacao()}</div>
        </div>
      );
    }
    const despesasProcessadas = agruparDespesasPorNumeroDocumento(registros);
    return (
      <div>
        {despesasProcessadas.map((despesa, index) => {
          return (
            <ItemListaDespesaGroupPay
              key={despesa.id || index}
              despesa={despesa}
              isHistorico={isHistorico}
            />
          );
        })}
        <div className="mt-4 d-flex justify-content-end">{renderPaginacao()}</div>
      </div>
    );
  };

  const renderPaginacao = () => {
    return (
      <>
        {data.payload.totalPages > 1 && (
          <Pagination
            page={page}
            size="small"
            count={data.payload.totalPages}
            shape="rounded"
            variant="text"
            onChange={(e, paginaAtual) => {
              setPage(paginaAtual);
            }}
          />
        )}
      </>
    );
  };

  return (
    <Fade in={true} timeout={700}>
      <div>
        {renderContentListaDespesasGroupPay()}
      </div>
    </Fade>
  );
};

export default TelaListaDespesasGroupPay;
